import { CLASS_NAMES, EVENTS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * A class for the Header module
 */
export default class Header extends MessageBus {
  /**
   * Constructor function of the Header class.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();
    this.element = element;

    // Initialize the view
    this.init();
  }

  /**
   * Initializes the view by calling the functions to
   * create DOM references, setup event handlers, and
   * create the event listeners.
   * @returns {object} Header A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();
    return this;
  }

  /**
   * Cache DOM References
   * Find all necessary DOM elements used in the view and cache them.
   * @returns {object} Header A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.headerContainer = this.element.querySelector(
      `.${CLASS_NAMES.CONTAINER}`
    );
    this.body = document.body;
    this.nav = this.element.querySelector(CLASS_NAMES.NAV);
    this.headerBranding = this.element.querySelector(
      `.${CLASS_NAMES.HEADER__BRANDING}`
    );
    this.headerSearchInput = this.element.querySelector(
      `.${CLASS_NAMES.HEADER__SEARCH_INPUT}`
    );
    this.headerSearchIcon = this.element.querySelector(
      `.${CLASS_NAMES.HEADER__SEARCH_ICON}`
    );
    this.headerHamburger = this.element.querySelector(
      `.${CLASS_NAMES.HEADER__HAMBURGER}`
    );
    this.headerMobile = this.element.querySelector(
      `.${CLASS_NAMES.HEADER__MOBILE}`
    );
    this.headerNavItems = this.element.querySelectorAll(
      `.${CLASS_NAMES.HEADER__NAV_ITEM_MOBILE}`
    );
    this.headerDonateLink = this.element.querySelector(
      `.${CLASS_NAMES.HEADER__DONATE_LINK}`
    );
    this.headerDonateLinkClose = this.element.querySelector(
      `.${CLASS_NAMES.HEADER__DONATE_LINK_CLOSE}`
    );

    return this;
  }

  /**
   * Create event handlers to enable interaction with view.
   * @returns {object} Header A reference to the current instance of the class
   * @chainable
   */
  enable() {
    if (this.headerHamburger) {
      this.headerHamburger.addEventListener(
        EVENTS.CLICK,
        this.toggleHamburgerMenu.bind(this)
      );
    }

    if (this.headerSearchIcon) {
      this.headerSearchIcon.addEventListener(
        EVENTS.CLICK,
        this.toggleSearch.bind(this)
      );
    }

    if (this.headerDonateLink) {
      this.headerDonateLink.addEventListener(
        EVENTS.CLICK,
        this.toggleDonate.bind(this)
      );
    }

    if (this.headerDonateLinkClose) {
      this.headerDonateLinkClose.addEventListener(
        EVENTS.CLICK,
        this.closeDonateTable.bind(this)
      );
    }

    if (this.headerNavItems) {
      this.headerNavItems.forEach((item) => {
        item.addEventListener(EVENTS.CLICK, this.toggleMobileNav.bind(this));
      });
    }

    window.addEventListener(EVENTS.SCROLL, () => {
      this.element.classList.toggle(CLASS_NAMES.STICKY, window.scrollY > 0);
    });

    return this;
  }

  /**
   * Sets up event handlers specific to the Header component.
   * Currently, this is a placeholder method that can be extended
   * to include additional event handler bindings in the future.
   * @returns {object} Header A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    return this;
  }

  /**
   * Toggles the mobile hamburger menu.
   * @param {Event} event - The click event
   */
  toggleHamburgerMenu(event) {
    event.stopPropagation();
    this.headerHamburger.classList.toggle(CLASS_NAMES.HEADER__HAMBURGER_ACTIVE);
    this.headerMobile.classList.toggle(CLASS_NAMES.HEADER__MOBILE_ACTIVE);
    this.headerContainer.classList.toggle(CLASS_NAMES.ACTIVE);
  }

  /**
   * Toggles the search input field.
   * @param {Event} event - The click event
   */
  toggleSearch(event) {
    event.stopPropagation();
    this.headerSearchInput.classList.toggle(
      CLASS_NAMES.HEADER__SEARCH_INPUT_ACTIVE
    );
  }

  /**
   * Toggles the donate section.
   * @param {Event} event - The click event
   */
  toggleDonate(event) {
    event.stopPropagation();
    this.headerDonateLink.classList.toggle(
      CLASS_NAMES.HEADER__DONATE_LINK_CLICKED
    );
  }

  /**
   * Closes the donate table for mobile view.
   */
  closeDonateTable() {
    this.headerDonateFull.classList.remove(
      CLASS_NAMES.HEADER__DONATE_LINK_FULL_HOVER
    );
    this.headerDonateLink.classList.remove(
      CLASS_NAMES.HEADER__DONATE_LINK_CLICKED
    );
  }

  /**
   * Toggles navigation items for mobile menu.
   * @param {Event} event - The click event
   * @returns {object} Header A reference to the current instance of the class
   */
  toggleMobileNav(event) {
    const menuItem = event.currentTarget.nextElementSibling;
    event.currentTarget.classList.toggle(CLASS_NAMES.ACTIVE);
    if (
      menuItem.classList.contains(
        CLASS_NAMES.HEADER__NAVIGATION_CHILDREN_ACTIVE
      )
    ) {
      menuItem.classList.remove(CLASS_NAMES.HEADER__NAVIGATION_CHILDREN_ACTIVE);
    } else {
      menuItem.classList.add(CLASS_NAMES.HEADER__NAVIGATION_CHILDREN_ACTIVE);
    }
    return this;
  }
}
